import { Injectable, Logger } from '@nestjs/common';
import { createHash } from 'node:crypto';
import { decryptWithKey, encryptWithKey } from './envelope';

export const KEY_PROVIDER = Symbol('KEY_PROVIDER');

/**
 * Supplies the master key (KEK) that wraps each organization's data key.
 *
 * An interface rather than a direct `process.env` read because the choice of
 * where the KEK lives is the difference between "our admins promise they
 * didn't look" and "here is an external decrypt log they cannot edit"
 * (docs/19-media-management-plan.md §10.3). Today it is an env-held key; a KMS
 * or Vault transit backend is a second implementation of this interface and
 * nothing else changes.
 */
export interface KeyProvider {
  /** Wrap an organization data key. */
  wrap(dataKey: Buffer, context: string): Promise<string>;
  /** Unwrap it again. Every call is auditable at the vault layer. */
  unwrap(wrapped: string, context: string): Promise<Buffer>;
  /** Identifies which KEK produced a wrap, so rotation can find stale rows. */
  keyId(): string;
}

const ENV_KEY = 'STORAGE_SECRET_KEY';

/**
 * KEK held in the process environment, sourced from the VPS secrets file
 * (plan.md §6.2). Never in the repo, never in the database, and — verify this
 * when setting up backups — never inside a `db-backups` dump, since a database
 * backup that also carries the key is plaintext with extra steps.
 */
@Injectable()
export class EnvKeyProvider implements KeyProvider {
  private readonly logger = new Logger(EnvKeyProvider.name);
  private cached?: Buffer;

  /**
   * Resolved lazily, not in the constructor: the API boots in environments
   * that never touch merchant storage (tests, CI, a fresh dev box), and
   * refusing to start there would be a worse failure than refusing at the
   * point a credential is actually encrypted.
   */
  private master(): Buffer {
    if (this.cached) return this.cached;
    const raw = process.env[ENV_KEY];
    if (!raw) {
      throw new Error(
        `${ENV_KEY} is not set. Merchant storage credentials cannot be encrypted or read without it.`
      );
    }
    const key = Buffer.from(raw, 'base64');
    if (key.length !== 32) {
      throw new Error(`${ENV_KEY} must be 32 bytes, base64-encoded (got ${key.length}).`);
    }
    this.cached = key;
    return key;
  }

  async wrap(dataKey: Buffer, context: string): Promise<string> {
    return encryptWithKey(this.master(), dataKey, context);
  }

  async unwrap(wrapped: string, context: string): Promise<Buffer> {
    return decryptWithKey(this.master(), wrapped, context);
  }

  keyId(): string {
    // A fingerprint, not the key: safe to log, safe to store on a row, and
    // enough to tell whether a ciphertext predates a KEK rotation.
    return createHash('sha256').update(this.master()).digest('hex').slice(0, 16);
  }

  /** True when the platform is configured to hold merchant credentials at all. */
  isConfigured(): boolean {
    try {
      this.master();
      return true;
    } catch {
      return false;
    }
  }
}
